import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Users, Award, Building, Globe, Handshake } from 'lucide-react';

const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_5c336d28-624d-4e58-a645-2e22616953a9/artifacts/o2manlku_457a83be-a121-45ac-b501-e02468665fd8.png";

const values = [
  { icon: Heart, title: 'Compassion', description: 'We act with empathy and care for every individual we serve.' },
  { icon: Users, title: 'Inclusion', description: 'We embrace diversity and ensure no one is left behind.' },
  { icon: Award, title: 'Excellence', description: 'We strive for the highest standards in all our work.' },
  { icon: Building, title: 'Transparency', description: 'We operate with openness and accountability.' },
];

const milestones = [
  { year: '2005', title: 'Foundation', description: 'OPAD was established with a vision to alleviate poverty globally.' },
  { year: '2010', title: 'First African Office', description: 'Expanded operations to East Africa, reaching thousands.' },
  { year: '2015', title: 'UN Partnership', description: 'Became a recognized partner of the United Nations.' },
  { year: '2020', title: 'Global Impact', description: 'Reached 50,000+ beneficiaries across 15 countries.' },
  { year: '2024', title: 'Sustainability Focus', description: 'Launched major climate action initiatives.' },
];

const partners = [
  { name: 'United Nations', type: 'Strategic Partner' },
  { name: 'World Bank', type: 'Funding Partner' },
  { name: 'European Union', type: 'Institutional Partner' },
  { name: 'African Union', type: 'Regional Partner' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6B9B7A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#7BC4E2]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                About OPAD
              </h1>
              <p className="text-lg text-white/90 leading-relaxed">
                The Organization for Poverty Alleviation and Development (OPAD) is an international humanitarian and development NGO dedicated to empowering communities, reducing poverty, and promoting sustainable development through education, climate action, health, and humanitarian programmes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden" id="mission">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/15 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
          <div className="absolute top-[40%] right-[10%] w-[150px] h-[150px] bg-[#E5B75F]/15 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-[#7BC4E2]/20"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="mission-card"
            >
              <div className="w-16 h-16 bg-[#7BC4E2] rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-4">Our Mission</h2>
              <p className="text-slate-700 leading-relaxed text-lg mb-4">
                OPAD's mission is to eradicate poverty and empower vulnerable communities through sustainable development, climate action, education, and humanitarian support worldwide.
              </p>
              <p className="text-[#4A7C59] font-medium italic border-l-4 border-[#4A7C59] pl-4">
                Creating lasting impact through community-driven solutions and global partnerships.
              </p>
            </motion.div>

            <motion.div
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-[#E08B7A]/20"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="vision-card"
            >
              <div className="w-16 h-16 bg-[#E08B7A] rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-4">Our Vision</h2>
              <p className="text-slate-700 leading-relaxed text-lg mb-4">
                A world where every person has access to education, healthcare, economic opportunities, and a safe environment to thrive.
              </p>
              <p className="text-[#E08B7A] font-medium italic border-l-4 border-[#E08B7A] pl-4">
                Building a future where no one is left behind.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-[#E08B7A]/15 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#4A7C59]/10 rounded-tr-[180px]" />
          <div className="absolute top-[30%] left-[20%] w-[120px] h-[120px] bg-[#E5B75F]/20 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">Our Values</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These core principles guide everything we do at OPAD.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#4A7C59]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-[#4A7C59]" />
                </div>
                <h3 className="text-lg font-bold font-['Manrope'] text-slate-900 mb-2">{value.title}</h3>
                <p className="text-sm text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline with Abstract Background */}
      <section className="relative section-padding overflow-hidden" id="history">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/15 rounded-br-[180px]" />
          <div className="absolute bottom-0 right-0 w-[450px] h-[400px] bg-[#E5B75F]/15 rounded-tl-[200px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">Our Journey</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Key milestones in our mission to create lasting change.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-[#7BC4E2]/30 hidden md:block" />

            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`milestone-${index}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100">
                      <span className="text-[#7BC4E2] font-bold text-xl">{milestone.year}</span>
                      <h3 className="text-lg font-bold font-['Manrope'] text-slate-900 mt-2">{milestone.title}</h3>
                      <p className="text-slate-600 mt-1">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-[#7BC4E2] rounded-full border-4 border-white shadow-lg z-10 hidden md:block" />
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden" id="leadership">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#4A7C59]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#E08B7A]/15 rounded-tr-[150px]" />
          <div className="absolute top-[50%] left-[5%] w-[100px] h-[100px] bg-[#7BC4E2]/20 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Governance & Leadership
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                OPAD is governed by a dedicated Board of Directors comprising experienced professionals from diverse backgrounds including international development, finance, law, and academia. Our leadership team brings decades of combined experience in humanitarian work.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Globe className="w-6 h-6 text-[#7BC4E2]" />
                  <span className="text-slate-700">International Board of Directors</span>
                </div>
                <div className="flex items-center gap-4">
                  <Users className="w-6 h-6 text-[#7BC4E2]" />
                  <span className="text-slate-700">Experienced Executive Team</span>
                </div>
                <div className="flex items-center gap-4">
                  <Award className="w-6 h-6 text-[#7BC4E2]" />
                  <span className="text-slate-700">Independent Oversight Committee</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={TEAM_IMAGE}
                alt="OPAD Leadership Team"
                className="rounded-2xl shadow-lg w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* EU Humanitarian Certification & Global Engagement */}
      <section className="py-12 bg-gradient-to-r from-[#003399]/5 via-white to-[#E5B75F]/10" data-testid="eu-recognition-band">
        <div className="container-custom">
          <motion.div
            className="max-w-5xl mx-auto bg-white rounded-3xl shadow-lg border border-slate-100 p-8 grid md:grid-cols-3 gap-6 items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 bg-[#003399] text-white text-xs font-semibold rounded-full px-3 py-1 mb-3">
                <span>🇪🇺</span> EU Humanitarian Certification & Global Engagement
              </div>
              <h3 className="text-xl md:text-2xl font-bold font-['Manrope'] text-slate-900 mb-2">
                Recognized at the highest international levels
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3">
                OPAD holds <strong>UN ECOSOC Consultative Status</strong>, is a member of the <strong>UN Global Compact</strong> and the <strong>Global Nutrition Cluster</strong>, and is registered in the
                {' '}<strong>EU Transparency Register</strong> (ID: 026269897821-30) — collaborating actively with UNDP, UNHCR, FAO, OHCHR, UNODC, UNFCCC, and the Adaptation Fund.
              </p>
              <Link to="/policy-engagement" className="inline-flex items-center gap-2 text-sm font-semibold text-[#003399] hover:text-[#0055cc]">
                See full institutional engagement <span aria-hidden>→</span>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-[#003399]/10 rounded-xl p-3">
                <div className="text-2xl font-bold text-[#003399]">UN</div>
                <p className="text-[10px] text-slate-500 mt-1 leading-tight">ECOSOC Consultative Status</p>
              </div>
              <div className="bg-[#E5B75F]/15 rounded-xl p-3">
                <div className="text-2xl font-bold text-[#A47A1F]">EU</div>
                <p className="text-[10px] text-slate-500 mt-1 leading-tight">Transparency Register</p>
              </div>
              <div className="bg-[#4A7C59]/10 rounded-xl p-3">
                <div className="text-2xl font-bold text-[#4A7C59]">UNGC</div>
                <p className="text-[10px] text-slate-500 mt-1 leading-tight">Global Compact Member</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partners Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden" id="partners">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[400px] bg-[#E5B75F]/15 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#7BC4E2]/15 rounded-tl-[180px]" />
          <div className="absolute top-[40%] right-[15%] w-[140px] h-[140px] bg-[#E08B7A]/10 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Partners & Strategic Alliances
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We collaborate with municipalities, NGOs, and international organizations across Europe and beyond to maximize our impact.
            </p>
          </div>

          {/* Municipal Partners */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Municipal Partners</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { name: 'Freguesia de Buarcos', country: 'Portugal', flag: '🇵🇹' },
                { name: 'Municipality of Héhalom', country: 'Hungary', flag: '🇭🇺' },
                { name: 'Municipality of Safi', country: 'Malta', flag: '🇲🇹' },
                { name: 'Municipality of Rezekne', country: 'Latvia', flag: '🇱🇻' },
                { name: 'Municipality of Suhindol', country: 'Bulgaria', flag: '🇧🇬' },
                { name: 'Municipality of Lwowek Slaski', country: 'Poland', flag: '🇵🇱' },
                { name: 'Municipality of Curtea de Arges', country: 'Romania', flag: '🇷🇴' },
                { name: 'Municipality of Klanjec', country: 'Croatia', flag: '🇭🇷' },
                { name: 'Municipality of Busince', country: 'Slovakia', flag: '🇸🇰' },
              ].map((partner, index) => (
                <motion.div
                  key={partner.name}
                  className="bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-md border border-slate-100 text-center hover:shadow-lg transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="text-2xl mb-2 block">{partner.flag}</span>
                  <h4 className="font-semibold text-slate-900 text-sm leading-tight mb-1">{partner.name}</h4>
                  <p className="text-xs text-slate-500">{partner.country}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* NGO & Organizational Partners */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">NGO & Organizational Partners</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { name: 'One Terrene International Group', country: 'Cyprus', flag: '🇨🇾' },
                { name: 'OPAD Finland', country: 'Finland', flag: '🇫🇮' },
                { name: 'C.E.P.H.I.', country: 'Italy', flag: '🇮🇹', subtitle: 'Centro Educativo di Progettazione e Hosting' },
                { name: 'Association pour le Soutien à la Citoyenneté Européenne', country: 'France', flag: '🇫🇷' },
                { name: 'Asociación Xuvenil Vrenza', country: 'Spain', flag: '🇪🇸' },
              ].map((partner, index) => (
                <motion.div
                  key={partner.name}
                  className="bg-[#4A7C59]/5 p-4 rounded-xl border border-[#4A7C59]/20 text-center hover:bg-[#4A7C59]/10 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="text-2xl mb-2 block">{partner.flag}</span>
                  <h4 className="font-semibold text-slate-900 text-sm leading-tight mb-1">{partner.name}</h4>
                  <p className="text-xs text-slate-500">{partner.country}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* International & Academic Partners */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">International & Academic Partners</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { name: 'Harbour Education', country: 'United Kingdom', flag: '🇬🇧', subtitle: 'Academic & Education Partner' },
              ].map((partner, index) => (
                <motion.div
                  key={partner.name}
                  className="bg-[#7BC4E2]/5 p-4 rounded-xl border border-[#7BC4E2]/30 text-center hover:bg-[#7BC4E2]/10 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="text-2xl mb-2 block">{partner.flag}</span>
                  <h4 className="font-semibold text-slate-900 text-sm leading-tight mb-1">{partner.name}</h4>
                  <p className="text-xs text-slate-500">{partner.country}</p>
                  {partner.subtitle && <p className="text-[10px] text-[#3A8FB5] mt-1 font-medium">{partner.subtitle}</p>}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Partner Stats */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <motion.div
              className="bg-[#4A7C59]/5 p-6 rounded-2xl text-center border-l-4 border-[#4A7C59]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-3xl font-bold text-[#4A7C59] mb-2">14+</div>
              <div className="font-semibold text-slate-900">Partners</div>
              <p className="text-sm text-slate-500 mt-1">Across 12 European countries</p>
            </motion.div>
            <motion.div
              className="bg-[#7BC4E2]/5 p-6 rounded-2xl text-center border-l-4 border-[#7BC4E2]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-3xl font-bold text-[#7BC4E2] mb-2">9</div>
              <div className="font-semibold text-slate-900">Municipalities</div>
              <p className="text-sm text-slate-500 mt-1">Local government partnerships</p>
            </motion.div>
            <motion.div
              className="bg-[#E08B7A]/5 p-6 rounded-2xl text-center border-l-4 border-[#E08B7A]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-3xl font-bold text-[#E08B7A] mb-2">EU</div>
              <div className="font-semibold text-slate-900">Funded Projects</div>
              <p className="text-sm text-slate-500 mt-1">Erasmus+ programmes</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
